import axios from "axios";
import store from "@/store";
import commonFunction from "../mixin/commonFunction";
import router from "@/router";
import { isPublicRoute } from "@/utils/routeUtils";

export const authClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL + "/api",
  withCredentials: true, // required to handle the CSRF token
});

/*
 * Add a response interceptor
 */
let isRefreshing = false;
let subscribers = [];

function onRefreshed() {
  subscribers.forEach((callback) => callback());
  subscribers = [];
}

function addSubscriber(callback) {
  subscribers.push(callback);
}

authClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    if (response?.status === 401 && !config._retry) {
      config._retry = true;

      return new Promise((resolve, reject) => {
        addSubscriber(() => {
          resolve(authClient(config));
        });

        if (!isRefreshing) {
          isRefreshing = true;

          (async () => {
            try {
              await store.dispatch("auth/refreshToken");
              isRefreshing = false;
              onRefreshed(); // Retry all requests in queue
            } catch (err) {
              isRefreshing = false;
              await store.dispatch("auth/logout");
              if (!isPublicRoute()) {
                router.push({ name: "login" });
              }
              reject(err);
            }
          })();
        }
      });
    }

    return Promise.reject(error);
  }
);

export default {
  refreshToken() {
    return authClient.post("/refresh-token")
      .then((response) => {
        return response;
      })
      .catch((err) => {
        const message = err?.response?.data?.message || err.message;
  
        // Check if token is missing or invalid
        if (message === "Refresh token missing" || message === "Invalid refresh token") {
          // Dispatch logout and redirect
          store.dispatch("auth/logout").finally(() => {
            router.push("/user/login");
          });
        }
  
        throw err;
      });
  },
  async login(payload) {
    return authClient.post("/login", payload);
  },
  async registerUser(payload) {
    return authClient.post("/register", payload);
  },
  logout() {
    return authClient.get("/logout");
  },
  async forgotPassword(payload) {
    return authClient.post("/sendpasswordlink", payload);
  },
  async resetPassword(payload) {
    return authClient.post("/reset-password", payload);
  },
  async getAuthUser() {
    return authClient
      .get("/get-user",{ withCredentials: true })
      .then((response) => {
        const encrypted = response.data.data;
        const decrypted_json = commonFunction.methods.decodeAPIResponse(encrypted);
        response.data.user = decrypted_json.user;
        response.data.token = decrypted_json.token;
        response.data.client = decrypted_json.client;
        response.data.seats = decrypted_json.seats;
  
        return response;
      })
      .catch((error) => {
        // Check for 401 Unauthorized
        if (error.response && error.response.status === 401) {
          localStorage.clear();
  
          // Redirect to login
          router.push({name:"login"});
        } else {
          // You can log or rethrow for other error types
          throw error;
        }
      });
  },

  getAllClients() {
    return authClient.get("/clients", {
      withCredentials: true,
    });
  },

  ssoLogin(payload) {
    return authClient.post("/client/sso-login", payload, {
      withCredentials: true,
    });
  },

  getUserReportByEmail() {
    return axios.get(process.env.VUE_APP_API_URL + "/api/employee-report", {
      withCredentials: true,
    });
  },

  downloadZipFile(url) {
    return axios.get(url, {
      responseType: 'blob',
    });
  },
  userDomainCheck() {
      return authClient.get("/check-domain");
  },
  removeUser() {
      return authClient.get("/remove-user");
  },

};